// src/hooks/useClientForm.js
// Hook customizado para gerenciar estado de formularios de cliente (ADD e EDIT)

import { useState, useCallback, useMemo } from 'react';
import {
  INITIAL_FORM_STATE,
  validateClientForm,
  mapClientToFormData,
  mapFormDataToClient,
  detectClientChanges,
  formatChangesForLog
} from '../utils/clientFormUtils.js';

/**
 * Hook para gerenciar formulario de cliente
 * Substitui os 22 estados duplicados (new* e edit*) por um unico estado
 *
 * @param {string} language - Idioma para mensagens de validacao
 * @returns {Object} Estado e funcoes do formulario
 */
export const useClientForm = (language = 'pt') => {
  // Modo do formulario: null (fechado), 'add' (adicionando), 'edit' (editando)
  const [mode, setMode] = useState(null);

  // Dados do formulario
  const [formData, setFormData] = useState({ ...INITIAL_FORM_STATE });

  // Cliente sendo editado (para detectar mudancas)
  const [originalClient, setOriginalClient] = useState(null);

  // ID do cliente sendo editado
  const [activeClientId, setActiveClientId] = useState(null);

  // Estado de loading do submit
  const [saving, setSaving] = useState(false);

  /**
   * Abre formulario no modo ADD
   */
  const openAddMode = useCallback(() => {
    setFormData({ ...INITIAL_FORM_STATE });
    setOriginalClient(null);
    setActiveClientId(null);
    setMode('add');
  }, []);

  /**
   * Abre formulario no modo EDIT com dados do cliente
   * @param {Object} client - Cliente a ser editado
   */
  const openEditMode = useCallback((client) => {
    if (!client) return;

    const formValues = mapClientToFormData(client);
    setFormData(formValues);
    setOriginalClient(client);
    setActiveClientId(client.id);
    setMode('edit');
  }, []);

  /**
   * Fecha formulario e reseta estado
   */
  const closeForm = useCallback(() => {
    setFormData({ ...INITIAL_FORM_STATE });
    setOriginalClient(null);
    setActiveClientId(null);
    setMode(null);
    setSaving(false);
  }, []);

  /**
   * Atualiza um campo do formulario
   * @param {string} fieldName - Nome do campo
   * @param {any} value - Novo valor
   */
  const updateField = useCallback((fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  }, []);

  /**
   * Atualiza multiplos campos de uma vez
   * @param {Object} fields - Objeto com campos a atualizar
   */
  const updateFields = useCallback((fields) => {
    setFormData(prev => ({
      ...prev,
      ...fields
    }));
  }, []);

  /**
   * Atualiza campo aninhado (ex: restrictedTimes.enabled)
   * @param {string} parentField - Campo pai
   * @param {string} childField - Campo filho
   * @param {any} value - Novo valor
   */
  const updateNestedField = useCallback((parentField, childField, value) => {
    setFormData(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [childField]: value
      }
    }));
  }, []);

  /**
   * Valida formulario atual
   * @returns {Object} { isValid: boolean, error: string | null }
   */
  const validate = useCallback(() => {
    return validateClientForm(formData, language);
  }, [formData, language]);

  /**
   * Verifica se formulario eh valido
   */
  const isValid = useMemo(() => {
    return validateClientForm(formData, language).isValid;
  }, [formData, language]);

  /**
   * Retorna dados formatados para API (snake_case)
   */
  const getApiData = useCallback(() => {
    return mapFormDataToClient(formData);
  }, [formData]);

  /**
   * Detecta mudancas em relacao ao cliente original (modo edit)
   * @returns {Object} Objeto com campos alterados
   */
  const getChanges = useCallback(() => {
    if (!originalClient || mode !== 'edit') return {};
    return detectClientChanges(originalClient, formData);
  }, [originalClient, formData, mode]);

  /**
   * Verifica se houve mudancas (modo edit)
   */
  const hasChanges = useMemo(() => {
    if (!originalClient || mode !== 'edit') return false;
    return Object.keys(detectClientChanges(originalClient, formData)).length > 0;
  }, [originalClient, formData, mode]);

  /**
   * Retorna string formatada das mudancas para log
   */
  const getChangesLog = useCallback(() => {
    const changes = getChanges();
    return formatChangesForLog(changes);
  }, [getChanges]);

  /**
   * Helpers para checklist
   */
  const addChecklistItem = useCallback((item) => {
    setFormData(prev => ({
      ...prev,
      checklist: [...prev.checklist, item]
    }));
  }, []);

  const removeChecklistItem = useCallback((index) => {
    setFormData(prev => ({
      ...prev,
      checklist: prev.checklist.filter((_, i) => i !== index)
    }));
  }, []);

  const updateChecklistItem = useCallback((index, newItem) => {
    setFormData(prev => ({
      ...prev,
      checklist: prev.checklist.map((item, i) => i === index ? newItem : item)
    }));
  }, []);

  return {
    // Estado
    mode,
    formData,
    originalClient,
    activeClientId,
    saving,

    // Estado derivado
    isValid,
    hasChanges,
    isAddMode: mode === 'add',
    isEditMode: mode === 'edit',
    isOpen: mode !== null,

    // Acoes
    openAddMode,
    openEditMode,
    closeForm,
    updateField,
    updateFields,
    updateNestedField,
    setSaving,

    // Validacao e dados
    validate,
    getApiData,
    getChanges,
    getChangesLog,

    // Checklist helpers
    addChecklistItem,
    removeChecklistItem,
    updateChecklistItem
  };
};

export default useClientForm;
